import { _callFile, _callBackend } from '@/_api/api';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { Conversation } from '@/_types/models/conversations';

export const exportConversationAsCSV = async ({
  conversationId,
  start_date,
  end_date,
  token,
}: APIRequest<{
  conversationId: number;
  start_date: any;
  end_date: any;
  token: string;
}>): APIResponsePromise => {
  let endpoint = `/conversations/export_conversation?id=${conversationId}`;

  // Doing a check here to ensure we don't pass "undefined" as a string
  if (start_date && end_date) {
    endpoint = `${endpoint}&start_date=${start_date}&end_date=${end_date}`;
  }

  return await _callFile(
    endpoint,
    'GET',
    'application/vnd.ms-excel',
    `conversation-export-${conversationId}.csv`,
    token,
  );
};

export const exportConversationAsPDF = async ({
  conversationId,
  start_date,
  end_date,
  token,
}: APIRequest<{
  conversationId: number;
  start_date: any;
  end_date: any;
  token: string;
}>): APIResponsePromise => {
  let endpoint = `/conversations/export_conversation_as_pdf?id=${conversationId}`;

  if (start_date && end_date) {
    endpoint = `${endpoint}&start_date=${start_date}&end_date=${end_date}`;
  }

  return await _callBackend(endpoint, 'GET', null, token);
};

export const sendTestMassEmail = async ({
  body,
  subject,
  token,
}: APIRequest<{
  body: string;
  subject: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/advisors/send_test_mass_message',
    'POST',
    {
      body,
      subject,
    },
    token,
  );

export const sendMassEmail = async ({
  body,
  subject,
  clientIds,
  token,
}: APIRequest<{
  body: string;
  subject: string;
  clientIds: Array<string>;
}>): APIResponsePromise =>
  await _callBackend(
    '/advisors/send_mass_message',
    'POST',
    {
      body,
      subject,
      client_ids: clientIds,
    },
    token,
  );

export const getCompanyConversations = async ({
  companyId,
  token,
}: APIRequest<{
  companyId: number;
}>): APIResponsePromise<Array<Conversation>> =>
  await _callBackend(
    `/conversations/company_conversations?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

export const markAsUnseen = async ({
  id,
  token,
}: APIRequest<{
  id: number;
}>): APIResponsePromise =>
  await _callBackend('/conversations/mark_as_unseen', 'POST', { id }, token);

export const deleteMessage = async ({
  message_id,
  token,
}: APIRequest<{
  message_id: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/conversations/delete_scheduled_message',
    'POST',
    {
      message_id,
    },
    token,
  );

export const editMessage = async ({
  message_id,
  text,
  token,
}: APIRequest<{
  message_id: number;
  text: string;
}>): APIResponsePromise =>
  await _callBackend(
    '/conversations/edit_message',
    'POST',
    {
      message_id,
      text,
    },
    token,
  );

export const editScheduledMessage = async ({
  message_id,
  scheduled_time,
  token,
}: APIRequest<{
  scheduled_time: string;
  message_id: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/conversations/edit_scheduled_message',
    'POST',
    {
      message_id,
      scheduled_time,
    },
    token,
  );

export const updateConversation = async ({
  conversationId,
  advisor_ids,
  token,
}: APIRequest<{
  advisor_ids: Array<number>;
  conversationId: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/conversations/update_conversation',
    'POST',
    {
      id: conversationId,
      advisor_ids,
    },
    token,
  );

export const generateResponse = async ({
  conversationId,
  token,
}: APIRequest<{
  conversationId: number;
  token: string;
}>): APIResponsePromise =>
  await _callBackend(
    `/conversations/generate_response?conversation_id=${conversationId}`,
    'GET',
    null,
    token,
  );
